import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ArrowRight, Trophy, Lock, ShieldCheck, Star, RefreshCcw } from "lucide-react";
import { QuestionCard } from "@/components/QuestionCard";
import { useQuestions } from "@/hooks/useQuestions";
import { fisherYates } from "@/lib/examSelection";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import type { Question } from "@/lib/questions";
import { recordAnswer } from "@/lib/usage";
import { PREMIUM_CTA } from "@/lib/cta";
import { remarketingEngage } from "@/lib/remarketing";
import { track } from "@/lib/analytics";
import { UpgradeOffer } from "@/components/UpgradeOffer";
import { PlanPicker } from "@/components/PlanPicker";
import { SignupNudge } from "@/components/SignupNudge";
import { useAuth } from "@/hooks/useAuth";

const TOTAL_FREE = 5;
const URL = "https://alkoholprovet.se/gratis-alkoholprov";

const FAQ = [
  {
    q: "Är gratis alkoholprovet verkligen kostnadsfritt?",
    a: "Ja. Du kan göra 5 gratis frågor från vårt övningsmaterial inför kunskapsprovet i alkohollagen — utan registrering och utan kort.",
  },
  {
    q: "Vad är kunskapsprovet i alkohollagen?",
    a: "Kunskapsprovet är ett obligatoriskt prov för den som söker serveringstillstånd. Det testar kunskaper om alkohollagen, servering, tillsyn och ansvar.",
  },
  {
    q: "Räcker 5 gratis frågor för att klara kunskapsprovet?",
    a: "Nej. 5 frågor ger dig en uppfattning om svårighetsgrad och frågetyp. För att klara kunskapsprovet bör du öva på flera hundra frågor inom alla områden av alkohollagen.",
  },
  {
    q: "Kostar det något att öva mer?",
    a: "Du får tillgång till hela frågebanken med 800 frågor, förklaringar och provsimulering för 199 kr (engångsbetalning).",
  },
];

export const Route = createFileRoute("/gratis-alkoholprov")({
  component: GratisAlkoholprov,
  head: () => ({
    meta: [
      { title: "Gratis alkoholprov 2026 – alkoholtillstånd prov gratis online" },
      {
        name: "description",
        content:
          "Alkoholtillstånd prov gratis: gör 5 riktiga provfrågor om alkohollagen 2026 direkt i mobilen – utan kort och utan registrering. Se ditt resultat på 2 minuter.",
      },
      {
        name: "keywords",
        content:
          "alkoholtillstånd prov gratis, gratis alkoholprov, gratis frågor serveringstillstånd, kunskapsprov alkoholtillstånd gratis, alkohollagen prov gratis",
      },
      { property: "og:title", content: "Gratis alkoholprov 2026 – alkoholtillstånd prov gratis online" },
      {
        property: "og:description",
        content:
          "Testa dina kunskaper med 5 gratis frågor inför kunskapsprovet för serveringstillstånd. Inget kort, ingen registrering.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Gratis alkoholprov", url: URL },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

function GratisAlkoholprov() {
  const { user } = useAuth();
  const { questions: all, loading } = useQuestions({ freeOnly: true });
  const [queue, setQueue] = useState<Question[]>([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!all.length) return;
    setQueue(fisherYates(all).slice(0, TOTAL_FREE));
    setIdx(0);
    setScore(0);
    setDone(false);
    remarketingEngage("free_quiz");
  }, [all]);

  const current = queue[idx];
  const pct = useMemo(() => (queue.length ? Math.round((score / queue.length) * 100) : 0), [score, queue.length]);

  const handleAnswered = (correct: boolean) => {
    if (!current) return;
    recordAnswer({ id: current.id, correct, ts: Date.now(), category: current.category });
    if (correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (idx + 1 >= queue.length) {
      setDone(true);
      return;
    }
    setIdx((i) => i + 1);
  };

  const restart = () => {
    setQueue(fisherYates(all).slice(0, TOTAL_FREE));
    setIdx(0);
    setScore(0);
    setDone(false);
  };

  return (
    <div className="container-narrow py-8 sm:py-12">
      <Breadcrumbs items={[{ label: "Hem", to: "/" }, { label: "Gratis alkoholprov" }]} />

      {/* HERO */}
      <header className="mb-8">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-3 py-1 text-xs font-semibold mb-3">
          <CheckCircle2 className="h-3.5 w-3.5" /> 100% gratis · ingen registrering
        </div>
        <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-balance">
          Gratis alkoholprov 2026 – alkoholtillstånd prov gratis online
        </h1>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl">
          Testa dina kunskaper inför <strong>kunskapsprovet för alkoholtillstånd</strong> med 5 gratis frågor från vår
          frågebank. Frågorna är inom alkohollagen, serveringstillstånd och ansvar — precis som i det riktiga provet hos
          kommunen.
        </p>
      </header>

      {/* QUIZ / RESULT */}
      {!done && (
        <section aria-label="Gratis frågor">
          {loading || !current ? (
            <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground">
              Laddar frågor…
            </div>
          ) : (
            <>
              <div className="mb-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>Fråga {idx + 1} av {queue.length}</span>
                <span>Gratis frågor inför serveringstillstånd</span>
              </div>
              <QuestionCard
                key={current.id}
                q={current}
                index={idx}
                total={queue.length}
                onAnswered={handleAnswered}
                onNext={handleNext}
              />
            </>
          )}
        </section>
      )}

      {!done && idx >= 1 && (
        <UpgradeOffer
          source="free-quiz-inline"
          variant="strip"
          className="mt-6"
          heading="Vill du ha alla 800 frågor?"
        />
      )}

      {done && (
        <section aria-label="Resultat" className="rounded-3xl border border-border bg-card shadow-elevated overflow-hidden">
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 p-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
              <Trophy className="h-7 w-7" />
            </div>
            <h2 className="mt-4 font-display text-3xl font-bold">Bra jobbat!</h2>
            <p className="mt-2 text-muted-foreground">
              Du fick <strong className="text-foreground">{score} av {queue.length}</strong> rätt ({pct}%).
            </p>
          </div>

          <div className="p-6 sm:p-10">
            <div className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-card to-accent/5 p-6 sm:p-8">
              <h3 className="font-display text-xl sm:text-2xl font-bold">
                Vill du klara kunskapsprovet på första försöket?
              </h3>
              <p className="mt-2 text-muted-foreground">
                Lås upp <strong>800 frågor</strong> inom alla områden av alkohollagen, detaljerade förklaringar och
                full provsimulering. 92% av våra användare klarar provet.
              </p>
              <ul className="mt-5 grid gap-2.5 text-sm sm:grid-cols-2">
                {[
                  "Obegränsat med frågor",
                  "Förklaring till varje svar",
                  "Provsimulering med tidtagning",
                  "Statistik på dina svaga områden",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-muted-foreground">
                Engångsbetalning – välj hur länge du vill ha tillgång:
              </p>
              <PlanPicker source="gratis-alkoholprov-result" className="mt-3" />
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={restart}
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-5 py-3.5 text-sm font-medium hover:bg-secondary"
                >
                  <RefreshCcw className="h-4 w-4" /> Gör om gratistestet
                </button>
              </div>
              <Link to="/pricing" className="mt-4 inline-block text-xs text-accent underline underline-offset-2 hover:no-underline">
                Jämför planer (10, 30 och 90 dagar)
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Star className="h-3.5 w-3.5 text-primary fill-primary" /> 5.0/5 betyg</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-success" /> Säker betalning</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-success" /> 92% klarar provet</span>
              </div>
            </div>
            {!user && <SignupNudge source="mini-quiz" variant="soft" className="mt-6" />}
          </div>
        </section>
      )}

      {/* SEO INTRO BODY */}
      <section className="prose prose-neutral dark:prose-invert max-w-none mt-12">
        <h2>Varför göra ett gratis alkoholprov online?</h2>
        <p>
          Innan du betalar för kunskapsprovet hos kommunen vill du veta att du är förberedd. Med vårt{" "}
          <strong>gratis alkoholprov</strong> får du en känsla för hur frågorna är formulerade och vilken nivå provet
          ligger på. Frågorna täcker områden som ålderskontroll, serveringstider, ordning och nykterhet, marknadsföring
          och tillsyn.
        </p>
        <h2>Gratis frågor för serveringstillstånd – så fungerar det</h2>
        <ol>
          <li>Svara på 5 gratis frågor direkt — inget konto behövs.</li>
          <li>Få omedelbar förklaring till varje svar.</li>
          <li>Se ditt resultat och få en rekommendation om vad du bör fokusera på.</li>
        </ol>
        <h2>Kunskapsprov för alkoholtillstånd – gratis förberedelse</h2>
        <p>
          Kunskapsprovet är obligatoriskt för alla som söker serveringstillstånd enligt alkohollagen. Att börja med ett{" "}
          <Link to="/gratis-alkoholprov">gratis alkoholprov</Link> är ett enkelt sätt att se var du står. Vill du läsa
          mer om vad som krävs har vi guider för{" "}
          <Link to="/serveringstillstand">serveringstillstånd</Link>,{" "}
          <Link to="/kunskapsprov-alkohollagen">kunskapsprovet</Link> och{" "}
          <Link to="/ovningsprov-serveringstillstand">övningsprovet</Link>.
        </p>
      </section>

      {/* INTERNAL LINKS */}
      <section className="mt-10 rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl font-bold">Fortsätt läsa</h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2 text-sm">
          {[
            { to: "/serveringstillstand-och-kunskapsprov", label: "Serveringstillstånd & kunskapsprov – samlad guide" },
            { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov alkohollagen" },
            { to: "/ovningsprov-serveringstillstand", label: "Övningsprov för serveringstillstånd" },
            { to: "/alkohollagen-fragor", label: "Frågor om alkohollagen" },
            { to: "/serveringstillstand", label: "Serveringstillstånd – komplett guide" },
            { to: "/nya-regler-serveringstillstand-2026", label: "Nya regler för serveringstillstånd 2026" },
          ].map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="inline-flex items-center gap-1.5 text-primary hover:underline">
                <ArrowRight className="h-3.5 w-3.5" /> {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <h2 className="font-display text-2xl sm:text-3xl font-bold">Vanliga frågor om gratis alkoholprov</h2>
        <div className="mt-5 grid gap-3">
          {FAQ.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-card p-5">
              <summary className="cursor-pointer list-none font-semibold flex items-center justify-between gap-3">
                {f.q}
                <span className="text-muted-foreground transition-transform group-open:rotate-45 text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mt-12 rounded-3xl bg-gradient-to-br from-primary to-accent p-8 sm:p-12 text-primary-foreground text-center shadow-elevated">
        <Lock className="h-8 w-8 mx-auto opacity-90" />
        <h2 className="mt-3 font-display text-2xl sm:text-3xl font-bold">Redo att klara kunskapsprovet?</h2>
        <p className="mt-2 opacity-90 max-w-xl mx-auto">
          Få full tillgång till frågebanken, förklaringar och provsimulering — engångsbetalning 199 kr.
        </p>
        <Link
          to={PREMIUM_CTA.to}
          className="mt-6 inline-flex items-center gap-2 rounded-xl bg-background text-foreground px-6 py-3.5 text-sm font-semibold hover:bg-background/90"
        >
          {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </div>
  );
}
